/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Link } from 'react-router-dom';
import PolicyLayout from '../../components/policies/PolicyLayout';

export const TermsPage = () => (
  <PolicyLayout
    title="Terms & Conditions"
    effectiveDate="1 February 2026"
    lastUpdated="1 February 2026"
  >
    <p>
      These Terms & Conditions (<strong>"Terms"</strong>) govern your access to and use of the
      StudioScheduler platform, websites, and services (together, the <strong>"Platform"</strong>).
      The Platform is operated by <strong>Borrow A Geek Limited</strong>, a company registered in
      Ireland, trading as <strong>StudioScheduler</strong>, with its registered office at 18 Henry
      Street, Kilrush, County Clare V15 CX71, Republic of Ireland
      (<strong>"StudioScheduler"</strong>, <strong>"we"</strong>, <strong>"our"</strong>, or{' '}
      <strong>"us"</strong>).
    </p>
    <p>
      By creating an account, purchasing a Class Pass, booking a class, or otherwise using the
      Platform, you agree to be bound by these Terms. If you do not accept these Terms, do not use
      the Platform.
    </p>

    <h2>1. Who we are and what the Platform does</h2>
    <p>
      StudioScheduler is a marketplace and management platform that connects clients
      (<strong>"you"</strong>, <strong>"Clients"</strong>) with independent fitness, wellness and
      lifestyle studios (<strong>"Studios"</strong>). The Platform enables Studios to publish class
      schedules, sell Class Passes and memberships, accept payments, and manage bookings; and enables
      Clients to discover Studios, book classes, and purchase Class Passes.
    </p>
    <p>
      Each Studio is an independent business operating under its own name, staffing, policies and
      pricing. Classes, sessions, and services listed on the Platform are provided <em>by</em> the
      Studios, not by StudioScheduler. StudioScheduler does not provide fitness instruction, health
      advice, or in-person services.
    </p>

    <h2>2. Eligibility & account</h2>
    <ul>
      <li>You must be at least 16 years old (or the age of digital consent in your country) to create an account.</li>
      <li>You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.</li>
      <li>You must provide accurate, current, and complete information and keep your account details up to date.</li>
      <li>You may not create an account using another person's identity, share your account, or transfer it without our written consent.</li>
    </ul>

    <h2>3. Bookings, Class Passes and payments</h2>
    <p>
      When you book a class or purchase a Class Pass through the Platform, you enter into a
      <strong> contract directly with the Studio</strong> that offers that class or Class Pass.
      StudioScheduler acts as a technology provider that facilitates the transaction; it is not a
      party to the contract between you and the Studio, except where explicitly stated.
    </p>
    <p>
      Payments are processed by our payment provider (Ryft Payments). By providing payment details
      you authorise us and Ryft to charge the payment method for the amounts you approve. Studios
      set their own prices, cancellation windows, and Class Pass expiry rules.
    </p>

    <h2>4. Refunds and cancellations</h2>
    <p>
      Refund and cancellation policies are set by each Studio. StudioScheduler does not offer
      refunds directly for Studio-sold products or services. See our{' '}
      <Link to="/refund-policy">Refund Policy</Link> for full details of how refunds are handled and
      how StudioScheduler acts as an escalation point when a dispute with a Studio cannot be
      resolved directly.
    </p>

    <h2>5. Acceptable use</h2>
    <p>
      You agree not to (and not to attempt to):
    </p>
    <ul>
      <li>Use the Platform for any unlawful, harmful, fraudulent, or abusive purpose;</li>
      <li>Impersonate any person or misrepresent your affiliation with any person or entity;</li>
      <li>Interfere with, disrupt, probe, or reverse-engineer the Platform, its infrastructure, or its security;</li>
      <li>Use the Platform to send unsolicited communications, harass Studios or other Clients, or scrape data;</li>
      <li>Circumvent booking limits, ClassPass expiry, waitlist ordering, or payment requirements.</li>
    </ul>

    <h2>6. Health & safety</h2>
    <p>
      You participate in classes and services offered by Studios at your own risk. You are
      responsible for ensuring you are physically able to safely participate in any activity. Consult
      a doctor if you have concerns. StudioScheduler is not liable for injury, loss, or damage
      arising from your participation in a Studio's classes or use of a Studio's facilities.
    </p>

    <h2>7. Content & intellectual property</h2>
    <p>
      StudioScheduler and its licensors retain all rights, title, and interest in the Platform
      (excluding content submitted by Studios and Clients). You may not copy, modify, distribute,
      sell, or lease any part of the Platform without our prior written consent. You retain
      ownership of content you submit but grant StudioScheduler a worldwide, non-exclusive,
      royalty-free licence to host, reproduce, and display that content solely to operate the
      Platform.
    </p>

    <h2>8. Third-party services</h2>
    <p>
      The Platform integrates with third-party services (including Ryft Payments, SMTP2GO, Brevo,
      ClickSend, and OpenStreetMap Nominatim). Your use of those services is subject to their own
      terms and privacy policies. StudioScheduler is not responsible for the availability, content,
      or practices of third-party services.
    </p>

    <h2>9. Suspension & termination</h2>
    <p>
      We may suspend or terminate your account, or restrict your access to the Platform, if we
      reasonably believe you have violated these Terms, our{' '}
      <Link to="/privacy">Privacy Policy</Link>, applicable law, or the rights of a Studio or another
      Client. You may close your account at any time by contacting{' '}
      <a href="mailto:support@studioscheduler.ie">support@studioscheduler.ie</a>.
    </p>

    <h2>10. Disclaimers</h2>
    <p>
      The Platform is provided on an "as is" and "as available" basis. To the maximum extent
      permitted by law, StudioScheduler disclaims all warranties, express or implied, including
      warranties of merchantability, fitness for a particular purpose, and non-infringement. We do
      not warrant that the Platform will be uninterrupted, error-free, or that any defect will be
      corrected.
    </p>

    <h2>11. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by law, StudioScheduler's total aggregate liability to you
      arising out of or in connection with your use of the Platform shall not exceed the greater of
      (a) the amounts paid by you to StudioScheduler (excluding amounts paid to Studios) in the 12
      months preceding the claim, or (b) €100. Nothing in these Terms excludes or limits liability
      for fraud, death or personal injury caused by negligence, or any liability that cannot lawfully
      be excluded.
    </p>

    <h2>12. Governing law & jurisdiction</h2>
    <p>
      These Terms are governed by the laws of the <strong>Republic of Ireland</strong>. Disputes
      arising out of or in connection with these Terms shall be subject to the exclusive
      jurisdiction of the Irish courts, without prejudice to your rights as a consumer under EU law
      to bring proceedings in the country in which you reside.
    </p>

    <h2>13. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. Material changes will be communicated by email
      or through the Platform at least 14 days before they take effect. Continued use of the
      Platform after the effective date constitutes acceptance of the updated Terms.
    </p>

    <h2>14. Contact</h2>
    <p>
      Questions about these Terms should be sent to{' '}
      <a href="mailto:support@studioscheduler.ie">support@studioscheduler.ie</a>, or by post to:
      Borrow A Geek Limited, 18 Henry Street, Kilrush, County Clare V15 CX71, Republic of Ireland.
    </p>
  </PolicyLayout>
);

export default TermsPage;
